import { useState, useCallback } from 'react';
import { useEditorStore } from '~/shell/Store';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { MediaFile } from '../components/MediaGrid';

type MediaType = 'image' | 'video' | 'document' | 'audio';
type MediaSource =
  | 'upload'
  | 'unsplash'
  | 'pexels'
  | 'easyllama'
  | 'dalle'
  | '';

export const useMediaFetch = (type: MediaType, source: MediaSource) => {
  const { apiService } = useEditorStore();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [noResults, setNoResults] = useState(false);

  const getMediaTypes = () => {
    switch (type) {
      case 'document':
        return 'documents';
      case 'image':
        return 'pictures';
      case 'video':
        return 'videos';
      case 'audio':
        return 'audios';
    }
  };

  const clearFiles = () => {
    setFiles([]);
    setNoResults(false);
  };

  const setLoadingValue = (value: boolean) => {
    setLoading(value);
  };

  const fetchMedia = useCallback(
    async (page: number, search?: string) => {
      if (!apiService) return;

      setLoading(true);
      setNoResults(false);

      try {
        type MediaApiResponse =
          | Record<string, MediaFile[]>
          | { error: boolean; message: string };
        let response: MediaApiResponse;

        if (source === 'unsplash' && search) {
          response = await apiService.getUnsplashImages(search, page);
          if ((response as Record<string, MediaFile[]>)?.pictures?.length) {
            setFiles(prev => [
              ...prev,
              ...(response as Record<string, MediaFile[]>).pictures
            ]);
            return true;
          }
        } else if (source === 'pexels') {
          if (search) {
            response = await apiService.getPexels(search, page);
          } else {
            response = await apiService.getMedia(
              getMediaTypes(),
              page,
              'pexels'
            );
          }
          if ((response as Record<string, MediaFile[]>)?.videos?.length) {
            setFiles(prev => [
              ...prev,
              ...(response as Record<string, MediaFile[]>).videos
            ]);
            return true;
          }
        } else {
          response = await apiService.getMedia(getMediaTypes(), page, source);
          const mediaKey = getMediaTypes();
          if ((response as Record<string, MediaFile[]>)?.[mediaKey]?.length) {
            setFiles(prev => [
              ...prev,
              ...(response as Record<string, MediaFile[]>)[mediaKey]
            ]);
            return true;
          }
        }

        if (!response || !Object.keys(response).length) {
          setNoResults(true);
          return false;
        }

        return false;
      } catch (error) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Error fetching media'
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [apiService, source, type]
  );

  const deleteMedia = useCallback(
    async (id: number) => {
      if (!apiService) return;

      try {
        const response = await apiService.deleteMedia(id, getMediaTypes());

        if (!response.error) {
          return response;
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: response.message
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    [apiService, type]
  );

  const resetState = useCallback(() => {
    setFiles([]);
    setNoResults(false);
    setLoading(false);
  }, []);

  return {
    files,
    loading,
    noResults,
    fetchMedia,
    deleteMedia,
    resetState,
    setFiles,
    setLoadingValue,
    clearFiles
  };
};
